import {
  ArrowRight,
  Basket,
  Gift,
  CalendarDots,
  Camera,
  CheckSquare,
  GraduationCap,
  Image as ImageIcon,
  ListChecks,
  Microphone,
  PencilSimple,
  PushPin,
  Signpost,
  Sparkle,
  Star,
  Stop,
  Ticket,
  Trash,
  X,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import {
  FAMILY_WISHES,
  LEISURE_TEXT,
  leftLabel,
  matchEvent,
  suggestIcon,
  WEEKDAY_SHORT,
  type Assignees,
  type MagicItem,
  type TimetableDay,
} from '@shh/shared';
import { Busy, Button, ChoiceGroup, Icon, Text, buttonStyles, cn } from '@shh/ui';
import { useRef, useState, type ChangeEvent } from 'react';
import { Checkbox, Label, TextArea, TextField as AriaTextField } from 'react-aria-components';
import { Link } from 'react-router';
import { useLens } from '../app/lens';
import {
  addToList,
  bringChange,
  friendlyError,
  magicAdd,
  saveEvent,
  saveIdea,
  saveNote,
  saveTimetable,
  saveTodo,
  saveVoucher,
  saveWish,
} from '../data/api';
import { dayLabel } from '../data/agenda';
import { eventsOn, kids, type E, timetableName } from '../data/derive';
import { useFamily } from '../data/family';
import { EventForm } from './EventForm';
import {
  applyEdit,
  applyTask,
  describeChange,
  describeTask,
  findCandidates,
  type Candidate,
} from './magicEdits';
import { locale } from '../lib/locale';
import { useDictation } from '../lib/useDictation';
import { magicText } from '../i18n/magic';
import { formsText } from '../i18n/forms';

type FamilyData = ReturnType<typeof useFamily>;

export { shrink } from '../lib/image';
import { shrink } from '../lib/image';

// ---------------------------------------------------------------------------------------------
// Suggestions → what the family sees and saves
// ---------------------------------------------------------------------------------------------

/** First names from the AI → member ids; none (or unknown) → the whole family. */
function assigneesOf(f: FamilyData, names: string[]): Assignees {
  const ids = names
    .map((n) => f.members.find((m) => m.name.toLowerCase() === n.trim().toLowerCase())?.id)
    .filter((id): id is string => Boolean(id));
  return ids.length ? [...new Set(ids)] : 'all';
}

interface Suggestion {
  key: string;
  item: MagicItem;
  /** paper calendar: already in the family calendar */
  match?: ReturnType<typeof matchEvent>;
  /** timetable: whose plan (member id) */
  childId?: string;
  /** change/delete: the existing entries that fit, and the one picked */
  candidates?: Candidate[];
  choice?: string;
  /** cannot be applied – the reason, shown instead of a checkbox tick */
  blocked?: string;
}

const KIND_ICON: Record<MagicItem['kind'], PhosphorIcon> = {
  event: CalendarDots,
  todo: ListChecks,
  shopping: Basket,
  note: PushPin,
  timetable: GraduationCap,
  task: Star,
  edit: PencilSimple,
  idea: Signpost,
  voucher: Ticket,
  wish: Gift,
};

/** Whose wish list: the first name the AI heard, none = the family's shared wishes. */
const wishOwner = (f: FamilyData, who: string[]) => {
  const a = assigneesOf(f, who);
  return a === 'all' ? FAMILY_WISHES : a[0]!;
};

/** "Wöchentlich · Di", "Werktags", "Monatlich" – the event form's own words for a series. */
function seriesLabel(r: Extract<MagicItem, { kind: 'event' }>['repeat']): string | undefined {
  if (!r) return undefined;
  const label =
    r.freq === 'daily'
      ? formsText.everyNDays(String(r.every ?? 1))
      : r.freq === 'monthly'
        ? formsText.monthly
        : r.freq === 'yearly'
          ? formsText.yearly
          : r.days.length === 7
            ? formsText.daily
            : r.days.join() === '1,2,3,4,5'
              ? formsText.weekdays
              : `${r.every && r.every > 1 ? formsText.everyNWeeks(String(r.every)) : formsText.weekly} · ${r.days
                  .map((d) => WEEKDAY_SHORT[locale][d - 1])
                  .join(', ')}`;
  return r.skipHolidays ? `${label} · ${formsText.pauseHolidays}` : label;
}

function describe(f: FamilyData, s: Suggestion): { title: string; detail?: string } {
  const it = s.item;
  const names = (who: string[]) =>
    who.length ? who.join(', ') : it.kind === 'event' ? magicText.everyone : undefined;
  switch (it.kind) {
    case 'event':
      return {
        title: it.title,
        detail: [
          dayLabel(f, it.date, true).label,
          seriesLabel(it.repeat),
          it.endDate ? magicText.until(dayLabel(f, it.endDate, true).label) : undefined,
          it.start ? magicText.clock(`${it.start}${it.end ? `–${it.end}` : ''}`) : magicText.allDay,
          names(it.who),
          it.bring.length ? magicText.bring(it.bring.join(', ')) : undefined,
          it.location,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'todo':
      return {
        title: it.title,
        detail: [
          it.due ? magicText.until(dayLabel(f, it.due, true).label) : undefined,
          names(it.who),
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'shopping':
      return {
        title: it.name,
        detail: [it.spec, f.shopping ? magicText.toBring : magicText.toList]
          .filter(Boolean)
          .join(' · '),
      };
    case 'note':
      return {
        title: it.text,
        detail: it.until ? magicText.until(dayLabel(f, it.until, true).label) : undefined,
      };
    case 'task':
      return { title: it.title, detail: describeTask(it) };
    case 'edit': {
      const target = s.candidates?.find((c) => c.id === s.choice)?.label ?? it.target;
      return {
        title: target,
        detail: it.action === 'delete' ? magicText.deleted : describeChange(f, it),
      };
    }
    case 'idea':
      return { title: it.title, detail: magicText.inFun(LEISURE_TEXT[locale].place[it.place]) };
    case 'voucher':
      return {
        title: it.title,
        detail: [
          it.value,
          it.until ? leftLabel(f.today, it.until, locale) : undefined,
          it.code ? magicText.code(it.code) : undefined,
          it.from ? magicText.from(it.from) : undefined,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'wish': {
      const owner = wishOwner(f, it.who);
      const name = f.members.find((m) => m.id === owner)?.name;
      return {
        title: it.title,
        detail: [name ? magicText.wishesOf(name) : magicText.familyWish, it.price]
          .filter(Boolean)
          .join(' · '),
      };
    }
    case 'timetable':
      return {
        title: magicText.timetableDays(timetableName(it.label), String(it.days.length)),
        detail: it.days
          .map((d) => `${WEEKDAY_SHORT[locale][d.weekday - 1]} ${d.start}–${d.end}`)
          .join(' · '),
      };
  }
}

async function apply(f: FamilyData, s: Suggestion) {
  const it = s.item;
  const hid = f.householdId;
  switch (it.kind) {
    case 'event':
      return saveEvent(hid, {
        title: it.title,
        icon: suggestIcon(it.title, 'calendar'),
        date: it.date,
        endDate: it.endDate,
        start: it.start,
        end: it.end,
        assignees: assigneesOf(f, it.who),
        bring: it.bring,
        location: it.location,
        remind: it.remind,
        // a series like the event form stores it (1.27)
        repeat: it.repeat,
        recurring: it.repeat ? true : undefined,
        createdBy: f.meId,
      });
    case 'todo':
      return saveTodo(hid, {
        title: it.title,
        icon: '',
        assignees: assigneesOf(f, it.who),
        due: it.due,
        createdBy: f.meId,
      });
    case 'shopping':
      // without Bring! it goes on the family's own list (1.20; before that it became a to-do)
      return f.shopping
        ? bringChange({ op: 'add', name: it.name, spec: it.spec })
        : addToList(hid, f.listItems, { name: it.name, spec: it.spec ?? '' }, f.meId ?? null);
    case 'note':
      return saveNote(hid, { text: it.text, until: it.until, createdBy: f.meId });
    case 'task':
      return applyTask(f, it, (names) => assigneesOf(f, names));
    case 'edit':
      if (!s.choice) throw new Error(magicText.notFoundShort);
      return applyEdit(f, it, s.choice, (names) => assigneesOf(f, names));
    case 'idea':
      return saveIdea(
        hid,
        { title: it.title, place: it.place },
        { createdBy: f.meId, heart: f.meId },
      );
    case 'voucher':
      return saveVoucher(
        hid,
        {
          title: it.title,
          value: it.value,
          until: it.until,
          code: it.code,
          from: it.from,
          forWhom: 'all',
        },
        f.meId,
      );
    case 'wish':
      return saveWish(
        hid,
        {
          owner: wishOwner(f, it.who),
          title: it.title,
          price: it.price,
          love: 1,
          occasion: 'any',
        },
        f.meId,
      );
    case 'timetable': {
      if (!s.childId) throw new Error(magicText.whoseTimetable);
      const days: Partial<Record<'1' | '2' | '3' | '4' | '5', TimetableDay>> = {};
      for (const d of it.days) {
        days[String(d.weekday) as '1'] = { start: d.start, end: d.end, lessons: d.lessons };
      }
      return saveTimetable(hid, { memberId: s.childId, label: it.label ?? 'Schule', days });
    }
  }
}

function toSuggestions(f: FamilyData, items: MagicItem[], canParent: boolean): Suggestion[] {
  return items.map((item, i) => {
    const s: Suggestion = { key: `${i}-${item.kind}`, item };
    // stars are the parents' business (rules agree)
    if ((item.kind === 'task' || (item.kind === 'edit' && item.what === 'task')) && !canParent) {
      s.blocked = magicText.tasksParents;
    }
    // teens keep their own list (rules agree); parents may add to anybody's
    if (item.kind === 'wish' && !canParent) {
      const owner = wishOwner(f, item.who);
      if (owner !== FAMILY_WISHES && owner !== f.meId) {
        s.blocked = magicText.ownWishes;
      }
    }
    if (item.kind === 'edit') {
      s.candidates = findCandidates(f, item);
      s.choice = s.candidates[0]?.id;
      if (!s.candidates.length) s.blocked ??= magicText.notFound;
    }
    if (item.kind === 'event') {
      s.match = matchEvent(item, eventsOn(f, item.date));
    }
    if (item.kind === 'timetable') {
      const name = item.who?.toLowerCase();
      s.childId = kids(f).find((k) => k.name.toLowerCase() === name)?.id;
    }
    return s;
  });
}

// ---------------------------------------------------------------------------------------------
// The panel in "＋ → Einfach sagen"
// ---------------------------------------------------------------------------------------------

export function MagicAdd({ onDone }: { onDone: () => void }) {
  const f = useFamily();
  const lens = useLens();
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<Awaited<ReturnType<typeof shrink>>>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [list, setList] = useState<Suggestion[]>();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Suggestion>();
  const [saved, setSaved] = useState(0);
  const ask = async (spoken?: string) => {
    setBusy(true);
    setError(undefined);
    try {
      const { items } = await magicAdd({
        text: (spoken ?? text).trim() || undefined,
        image: photo ? { mimeType: photo.mimeType, data: photo.data } : undefined,
      });
      const suggestions = toSuggestions(f, items, lens.canParent);
      setList(suggestions);
      // already in the calendar → not ticked; everything else is
      setPicked(
        new Set(
          suggestions.filter((s) => s.match?.status !== 'same' && !s.blocked).map((s) => s.key),
        ),
      );
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // "Stopp" only stops listening: a photo or another sentence may follow (Mike, 26.10.: "ich kann
  // kein Bild mehr hinzufügen oder noch einmal einen Satz ergänzen"). Sending straight away is
  // for a later hold-to-talk on ＋ (backlog).
  const dictation = useDictation(text, setText);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const touch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const pickPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      shrink(file).then(setPhoto, () => setError(magicText.badPhoto));
    }
  };

  if (!f.household?.ai?.enabled)
    return <MagicTeaser canSwitchOn={lens.canParent} onDone={onDone} />;

  const save = async () => {
    if (!list) return;
    setBusy(true);
    setError(undefined);
    let ok = 0;
    const failed: Suggestion[] = [];
    for (const s of list.filter((x) => picked.has(x.key))) {
      try {
        await apply(f, s);
        ok++;
      } catch {
        failed.push(s);
      }
    }
    setBusy(false);
    setSaved((n) => n + ok);
    if (failed.length) {
      setList(failed);
      setPicked(new Set(failed.map((s) => s.key)));
      setError(
        failed.length === 1 ? magicText.oneFailed : magicText.someFailed(String(failed.length)),
      );
    } else {
      setList([]);
    }
  };

  if (editing && editing.item.kind === 'event') {
    const it = editing.item;
    const draft: E = {
      id: '',
      title: it.title,
      icon: suggestIcon(it.title, 'calendar'),
      date: it.date,
      endDate: it.endDate,
      start: it.start,
      end: it.end,
      assignees: assigneesOf(f, it.who),
      bring: it.bring,
      location: it.location,
      repeat: it.repeat,
      recurring: it.repeat ? true : undefined,
      source: 'manual',
    };
    return (
      <EventForm
        draft={draft}
        onDone={() => {
          // saved from the form (or cancelled): back to the remaining suggestions
          setList((l) => l?.filter((s) => s.key !== editing.key));
          setEditing(undefined);
        }}
      />
    );
  }

  // ---- results ----
  if (list) {
    const n = list.filter((s) => picked.has(s.key)).length;
    return (
      <div className="flex flex-col gap-gap-lg">
        {saved > 0 && (
          <p role="status" className="font-semibold text-feedback-success">
            {saved === 1 ? magicText.savedOne : magicText.savedMany(String(saved))}
          </p>
        )}
        {list.length === 0 ? (
          saved === 0 && <Text>{magicText.nothingFound}</Text>
        ) : (
          <>
            <Text size="sm" tone="secondary">
              {magicText.check}
            </Text>
            <ul aria-label={magicText.suggestions} className="flex flex-col gap-gap-sm">
              {list.map((s) => (
                <SuggestionRow
                  key={s.key}
                  s={s}
                  f={f}
                  checked={picked.has(s.key)}
                  onCheck={(on) =>
                    setPicked((p) => {
                      const next = new Set(p);
                      if (on) next.add(s.key);
                      else next.delete(s.key);
                      return next;
                    })
                  }
                  onChild={(childId) =>
                    setList((l) => l?.map((x) => (x.key === s.key ? { ...x, childId } : x)))
                  }
                  onChoice={(choice) =>
                    setList((l) => l?.map((x) => (x.key === s.key ? { ...x, choice } : x)))
                  }
                  onEdit={s.item.kind === 'event' ? () => setEditing(s) : undefined}
                />
              ))}
            </ul>
          </>
        )}
        {error && (
          <p role="alert" className="font-semibold text-feedback-danger">
            {error}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-gap-sm">
          <Button
            variant="quiet"
            onPress={() => {
              setList(undefined);
              setPhoto(undefined);
              setText('');
            }}
          >
            {magicText.sayMore}
          </Button>
          {list.length > 0 ? (
            <Button
              icon={CheckSquare}
              isDisabled={
                busy ||
                n === 0 ||
                list.some((s) => picked.has(s.key) && s.item.kind === 'timetable' && !s.childId)
              }
              onPress={() => void save()}
            >
              {busy ? magicText.saving : magicText.take(String(n))}
            </Button>
          ) : (
            <Button onPress={onDone}>{magicText.done}</Button>
          )}
        </div>
      </div>
    );
  }

  // ---- input ----
  return (
    <div className="flex flex-col gap-gap-lg">
      <AriaTextField value={text} onChange={setText} className="flex flex-col gap-gap-xs">
        <Label className="text-sm font-semibold text-text-secondary">{magicText.what}</Label>
        <TextArea
          rows={4}
          maxLength={2000}
          placeholder={magicText.example}
          className="w-full rounded-control border border-border-default bg-surface-raised p-inset-sm text-text-primary outline-none placeholder:text-text-muted data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-focused:border-border-focus"
        />
      </AriaTextField>
      {/* the first words only count once the microphone runs – say when to start */}
      {dictation.state === 'starting' && <Busy label={magicText.micStarting} />}
      {dictation.state === 'listening' && <Busy variant="ai" label={magicText.speakNow} />}
      {dictation.blocked && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {magicText.micBlocked}
        </p>
      )}
      <div className="flex flex-wrap gap-gap-sm">
        {dictation.canSpeak && (
          <Button
            variant={dictation.listening ? 'primary' : 'secondary'}
            icon={dictation.listening ? Stop : Microphone}
            onPress={dictation.listen}
            aria-pressed={dictation.listening}
          >
            {dictation.listening ? magicText.stop : magicText.speak}
          </Button>
        )}
        {/* Phones and tablets: the camera directly – the plain picker only offered the gallery
            on Mike's tablet (02.10.). Computers have no camera choice, so only "auswählen". */}
        {touch && (
          <Button variant="secondary" icon={Camera} onPress={() => cameraInput.current?.click()}>
            {magicText.camera}
          </Button>
        )}
        <Button variant="secondary" icon={ImageIcon} onPress={() => fileInput.current?.click()}>
          {photo ? magicText.otherPhoto : magicText.pickPhoto}
        </Button>
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          // opened by the button above – not a control of its own
          hidden
          onChange={pickPhoto}
        />
        <input ref={fileInput} type="file" accept="image/*" hidden onChange={pickPhoto} />
      </div>
      {photo && (
        <div className="flex items-center gap-gap-md">
          <img
            src={photo.url}
            alt={magicText.photoAlt}
            className="size-avatar-xl rounded-control object-cover"
          />
          <Button variant="quiet" icon={X} onPress={() => setPhoto(undefined)}>
            {magicText.removePhoto}
          </Button>
        </div>
      )}
      <section aria-labelledby="sagen-beispiele" className="flex flex-col gap-gap-xs text-sm">
        <h3 id="sagen-beispiele" className="font-semibold text-text-secondary">
          {magicText.youCanSay}
        </h3>
        <ul className="flex flex-col gap-gap-xs text-text-muted">
          {magicText.examples.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </section>
      {/* always in sight like every form's buttons (Mike, 26.10.: "erst nach scrollen sichtbar") –
          the same bar as Actions in forms.tsx */}
      <div className="sticky -bottom-inset-lg z-10 -mx-inset-lg -mb-inset-lg flex flex-col gap-gap-sm border-t border-border-subtle bg-surface-overlay px-inset-lg pt-inset-sm pb-inset-lg">
        {error && (
          <p role="alert" className="font-semibold text-feedback-danger">
            {error}
          </p>
        )}
        {/* the AI at work, visibly (Mike, 24.10.: "KI-Verarbeitung animieren") */}
        {busy && <Busy variant="ai" label={magicText.reading} className="self-center" />}
        <div className="grid grid-cols-[auto_1fr] gap-gap-sm">
          <Button variant="secondary" onPress={onDone}>
            {magicText.cancel}
          </Button>
          <Button
            size="lg"
            icon={Sparkle}
            isDisabled={busy || (!text.trim() && !photo)}
            onPress={() => void ask()}
          >
            {magicText.ask}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SuggestionRow({
  s,
  f,
  checked,
  onCheck,
  onChild,
  onChoice,
  onEdit,
}: {
  s: Suggestion;
  f: FamilyData;
  checked: boolean;
  onCheck: (on: boolean) => void;
  onChild: (id: string) => void;
  onChoice: (id: string) => void;
  onEdit?: () => void;
}) {
  const { title, detail } = describe(f, s);
  const remove = s.item.kind === 'edit' && s.item.action === 'delete';
  const kind = remove
    ? { icon: Trash, label: magicText.delete }
    : { icon: KIND_ICON[s.item.kind], label: magicText.kinds[s.item.kind] };
  return (
    <li className="flex flex-col gap-gap-sm rounded-card bg-surface-sunken p-inset-sm">
      <div className="flex items-start gap-gap-sm">
        <Checkbox
          isSelected={checked}
          isDisabled={Boolean(s.blocked)}
          onChange={onCheck}
          aria-label={`${kind.label}: ${title}`}
          className="group flex min-h-touch-min min-w-0 flex-1 cursor-pointer items-start gap-gap-md outline-none data-disabled:cursor-not-allowed"
        >
          <span
            aria-hidden
            className={cn(
              'mt-1 grid size-icon-lg shrink-0 place-items-center rounded-control border-2 border-border-default',
              'group-data-selected:border-action-primary group-data-selected:bg-action-primary group-data-selected:text-text-on-action',
              'group-data-focus-visible:outline-3 group-data-focus-visible:outline-border-focus',
            )}
          >
            {checked && <Icon icon={CheckSquare} size="sm" weight="bold" />}
          </span>
          <span className="flex min-w-0 flex-col">
            <span
              className={cn(
                'flex items-center gap-gap-xs text-xs font-bold tracking-wide uppercase',
                remove ? 'text-feedback-danger' : 'text-text-secondary',
              )}
            >
              <Icon icon={kind.icon} size="sm" /> {kind.label}
            </span>
            <span className="font-semibold">{title}</span>
            {detail && <span className="text-sm text-text-secondary">{detail}</span>}
            {s.blocked && (
              <span className="text-sm font-semibold text-feedback-warning">{s.blocked}</span>
            )}
            {s.match?.status === 'same' && (
              <span className="text-sm font-semibold text-feedback-success">
                {magicText.inCalendar}
              </span>
            )}
            {s.match?.status === 'otherTime' && (
              <span className="text-sm font-semibold text-feedback-warning">
                {magicText.otherTime(s.match.start ?? '')}
              </span>
            )}
          </span>
        </Checkbox>
        {onEdit && (
          <Button variant="quiet" icon={ArrowRight} onPress={onEdit}>
            {magicText.change}
          </Button>
        )}
      </div>
      {s.candidates && s.candidates.length > 1 && (
        <ChoiceGroup
          label={magicText.whichOne}
          value={s.choice ?? ''}
          onChange={onChoice}
          choices={s.candidates.map((c) => ({ value: c.id, label: c.label }))}
        />
      )}
      {s.item.kind === 'timetable' && (
        <ChoiceGroup
          label={magicText.forWhom}
          value={s.childId ?? ''}
          onChange={onChild}
          choices={kids(f).map((k) => ({ value: k.id, label: k.name }))}
        />
      )}
      {s.item.kind === 'timetable' && f.timetables[s.childId ?? ''] && (
        <Text size="sm" tone="muted">
          {magicText.replaces}
        </Text>
      )}
    </li>
  );
}

/** KI-Helfer still off: the benefits first, and the way to switch it on (parents). */
function MagicTeaser({ canSwitchOn, onDone }: { canSwitchOn: boolean; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-gap-lg">
      <p className="font-display text-xl font-black">{magicText.teaserTitle}</p>
      <ul className="flex flex-col gap-gap-sm">
        {magicText.teaser.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      {canSwitchOn ? (
        <Link to="/einstellungen#ki" onClick={onDone} className={cn(buttonStyles(), 'self-start')}>
          {magicText.switchOn}
        </Link>
      ) : (
        <Text tone="secondary">{magicText.parentsSwitchOn}</Text>
      )}
    </div>
  );
}
